import { useMemo, type VoidFunctionComponent } from 'react';

import { BinaryFetcherName } from '../../../../../shared/fetchers';
import Locator from '../../../../../shared/Locator';
import { isFreeTier } from '../../../../../shared/subscriptions';
import { useParams } from '../../../../contexts/RouterContext';
import { window } from '../../../../globals';
import { useIsSecurityEnabled } from '../../../../helpers/features';
import { useReleaseGroupAllProjects } from '../../../../hooks/api/releaseGroups';
import { Action, ResourceType, useHasPermission } from '../../../../shared/permissions';
import Subnav from '../../../generic/Subnav';
import { useCanDisplaySecurityOptions } from '../../generic/hooks';
import { ReportCategories, ReportCategoryTitles } from '../../generic/ReportGenerationV2/utils';

/**
 * Mini hook which just memoizes the enabled report types based on the
 * security and PDF reports feature flag and and the projects fetcher types.
 */
const useEnabledReportTypes = () => {
  const isSecurityAndPDFReportsEnabled = useCanDisplaySecurityOptions();
  const isSecurityEnabled = useIsSecurityEnabled();
  const params = useParams<{ projectGroupId: number }>();
  const allProjectLocators = useReleaseGroupAllProjects(params.projectGroupId).data ?? [];
  const areAllProjectsBinary = allProjectLocators.every(loc => {
    const { fetcher } = Locator.parse(loc);
    return fetcher === BinaryFetcherName;
  });

  return useMemo(() => {
    const excludedReports: string[] = [];
    if (!isSecurityAndPDFReportsEnabled) {
      excludedReports.push(ReportCategories.Vulnerability);
    }
    if (!isSecurityEnabled || areAllProjectsBinary) {
      excludedReports.push(ReportCategories.RemediationGuidance);
    }

    return Object.entries(ReportCategoryTitles).filter(([key]) => !excludedReports.includes(key));
  }, [isSecurityAndPDFReportsEnabled, isSecurityEnabled, areAllProjectsBinary]);
};

type Props = {
  baseUrl: string;
  releaseId?: number;
};

/**
 * ReleaseGroupSubnavItemReports is the Subnav item for the reports section of a release group.
 */
const ReleaseGroupSubnavItemReports: VoidFunctionComponent<Props> = ({
  baseUrl,
  releaseId,
}) => {
  const shouldShowSettings = !isFreeTier({
    plan: window?.user?.organization?.stripe_plan,
    stripePriceLimit: window?.user?.organization?.stripePriceLimit,
  }); // For settings

  const enabledReportTypes = useEnabledReportTypes();

  const hasPermission = useHasPermission();
  const canGenerateReport = hasPermission({ resourceType: ResourceType.ReleaseGroupReportAny, action: Action.Create })
    || (releaseId && hasPermission({
      resourceType: ResourceType.ReleaseGroupReport,
      action: Action.Create,
      releaseGroupId: releaseId,
    }));

  if (!canGenerateReport) {
    return null;
  }

  return (
    <Subnav.Dropdown
      to={`${baseUrl}/reports`}
      navKey="reports"
    >
      <Subnav.Dropdown.RootItem>
        Reports
      </Subnav.Dropdown.RootItem>
      {enabledReportTypes.map(([reportType, title]) => (
        <Subnav.Dropdown.Item
          key={reportType}
          to={`${baseUrl}/reports/${reportType}`}
          navKey={`reports/${reportType}`}
        >
          {title}
        </Subnav.Dropdown.Item>
      ))}
      {shouldShowSettings && (
        <Subnav.Dropdown.Item
          to={`${baseUrl}/reports/settings`}
          navKey="reports/settings"
        >
          Settings
        </Subnav.Dropdown.Item>
      )}
    </Subnav.Dropdown>
  );
};

export default ReleaseGroupSubnavItemReports;
